import type { FilterConfig } from './types';

export const FILTER_CONFIGS: FilterConfig[] = [
  // Industry (Level-1) → Sub-industry (Level-2), two dependent dropdowns (NPJB-334).
  // Sub-industry options/disable are driven by the parent selection in filter-groups.
  //
  // NPJB-662 opened Industry to custom values: a brief naming an industry outside the
  // seeded taxonomy ("eCommerce") is now auto-created as a custom value rather than only
  // warned about.
  //
  // NPJB-608 considered opening Sub-industry too — the builder captures a *client's*
  // request and the client may run their own taxonomy, so an off-taxonomy Level-2 name is
  // arguably legitimate input. **Product declined**, so Sub-industry stays closed and
  // NPJB-334 AC4 still stands: a Level-2 value only means something under a Level-1 parent,
  // and a free-typed sub has no parent to hang off, so allowing it would either orphan the
  // value or force us to invent a parent mapping.
  //
  // The flag drives three behaviours at once, which is why it is not a UI-only switch: the
  // custom-value affordances in FilterDropdown (the pinned "+ Add custom value" row and the
  // search-triggered `Add "…"` row), the unmatched-label type in `resolveSelections`, and
  // the auto-create/warn split in `partitionUnmapped`. If Sub-industry is ever reopened,
  // note that the brief can return a sub whose parent it also failed to map — the dropdown
  // is safe because filter-groups disables it until an industry is selected, but the brief
  // path has no such guarantee and would need its own guard.
  //
  // ORDER MATTERS: `industry` stays ahead of `subIndustry`. Both the brief's resolve loop
  // and `partitionUnmapped`'s auto-create map apply dimensions in this array's order, so a
  // dependent dimension is always written after its parent.
  //
  // The Intelligence drawer's own registry (src/app/intelligence-audience/lib/filter-configs.ts)
  // deliberately keeps BOTH levels closed — its filters carry no custom-value concept and an off-taxonomy
  // name matches zero rows there. Do not mirror this flag across.
  { key: 'industry', apiType: 'industry', label: 'Industry', allowCustom: true, selectAll: true },
  {
    key: 'subIndustry',
    apiType: 'sub-industry',
    label: 'Sub-Industry',
    allowCustom: false,
    selectAll: true,
  },
  { key: 'seniority', apiType: 'seniority', label: 'Seniority', allowCustom: true },
  { key: 'jobFunction', apiType: 'job-function', label: 'Job Function', allowCustom: true },
  // NPJB-553: free-text job titles — no taxonomy, so no lookup and no options popup.
  // `apiType` is empty on purpose: JobTitleField renders instead of FilterDropdown, so
  // nothing ever issues GET audience/lookups/… for this key. Array position puts it
  // directly below Job Function and above Region once filter-groups splits the
  // company/contact groups.
  { key: 'jobTitle', apiType: '', label: 'Job Title', allowCustom: true },
  {
    key: 'employeeRange',
    apiType: 'employee-range',
    label: 'Employee Range',
    allowCustom: true,
    selectAll: true,
  },
  {
    key: 'revenueRange',
    apiType: 'revenue-range',
    label: 'Revenue Range',
    allowCustom: true,
    selectAll: true,
  },
  { key: 'geoRegion', apiType: 'region', label: 'Region', allowCustom: true },
  { key: 'country', apiType: 'country', label: 'Country', allowCustom: true, selectAll: true },
  // NPJB-559: the 50 U.S. states, from the audience_lookups 'state' catalog. Sits
  // last so it renders directly below Country, and is enabled only while
  // "United States" is among the selected countries (see filter-groups).
  // `allowCustom: false` unlike Country — this is a closed 50-value taxonomy, so a
  // free-typed value could only ever be invalid data.
  { key: 'state', apiType: 'state', label: 'State', allowCustom: false, selectAll: true },
];
